import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useUserRoles } from '@anytag/auth';
import { EngagementCampaignStatus } from '@anytag/graphql';
import {
  Label,
  Accordion,
  AccordionTrigger,
  AccordionPrimitive,
  AccordionContent,
  Icon,
  Form
} from '@anytag/shared-atoms';
import {
  useAllCountryOptions,
  useAllPicUserOptions,
  useAllAvailableCountryOptions,
  useAllCampaignCategoryOptions
} from '@anytag/shared-hooks';
import {
  TextField,
  SelectorField,
  TextareaField,
  RichEditorField,
  FileUploadField,
  RangeDatePickerField,
  SingleDatePickerField,
  MultipleSelectorFieldProps,
  AsyncSearchAdvertiserField,
  AsyncSearchHubspotDealField,
  MultipleAsyncSearchHubspotDealFieldProps
} from '@anytag/shared-molecules';
import { DisabledFields } from '@anytag/shared-types';
import { cn } from '@anytag/shared-utils';
import { EngagementCampaignFormKeys, EngagementCampaignFormValues } from '../types';

interface CampaignInformationProps {
  disabledFields: DisabledFields<EngagementCampaignFormKeys>;
}
export const CampaignInformation = ({ disabledFields }: CampaignInformationProps) => {
  const { t } = useTranslation();
  const {
    isAdminStaff,
    isAdvertiser,
    isAgencyTalentAgencyPartner,
    isAdminStaffTalentAgencyPartner,
    isAdminStaffAgencyTalentAgencyPartner
  } = useUserRoles();
  const { allCountryOptions } = useAllCountryOptions();
  const { allCampaignCategoryOptions } = useAllCampaignCategoryOptions();
  const { allAvailableCountryOptions } = useAllAvailableCountryOptions();
  const { allPicsUserOptions } = useAllPicUserOptions({ skip: !isAdminStaffTalentAgencyPartner });
  const {
    watch,
    setValue,
    formState: { defaultValues }
  } = useFormContext<EngagementCampaignFormValues>();

  const currencyValue = watch('currency');
  const canOnlyEditEndDate = [EngagementCampaignStatus.UPCOMING, EngagementCampaignStatus.FINISHED].includes(
    defaultValues?.status as EngagementCampaignStatus
  );

  const handleCountryIdsChange: MultipleSelectorFieldProps<EngagementCampaignFormKeys>['onChange'] = (
    name,
    selectedCountryIds
  ) => {
    setValue(name, selectedCountryIds, { shouldValidate: true });

    const { currency, needToSendCurrency } = (() => {
      if (!selectedCountryIds.length) {
        return { currency: '', needToSendCurrency: false };
      }

      if (selectedCountryIds.length === 1) {
        const country = allAvailableCountryOptions.find((country) => country.value === selectedCountryIds[0]);

        return { needToSendCurrency: !country, currency: country?.currency || currencyValue || 'USD' };
      }

      return { needToSendCurrency: true, currency: 'USD' };
    })();

    setValue('currency', currency, { shouldValidate: true });
    setValue('needToSendCurrency', needToSendCurrency);
  };

  const handleHubspotDealsChange: MultipleAsyncSearchHubspotDealFieldProps<EngagementCampaignFormKeys>['onChange'] = (
    name,
    value,
    options
  ) => {
    setValue(name, value);

    // TODO: We already changed the gql from HubspotDealsList to HubspotDealsListMcp which is still missing some fields so the logics here not working anymore
    // TODO: Recheck when BE has update about HubspotDealsListMcp
    // const hubspotDeal = options?.length === 1 ? options[0] : undefined;

    // if (isAddPage) {
    //   // this may not work cause ownerName from bigQuery may not match our PICs name
    //   const salesPicId = hubspotDeal?.ownerName
    //     ? allPicsUserOptions.find((el) => el.label === hubspotDeal?.ownerName)?.value
    //     : null;
    //   setValue('salesPics', salesPicId ? [salesPicId] : [], { shouldValidate: true });
    //   setValue('countryIds', [hubspotDeal?.country || ''], { shouldValidate: true });
    //   const isCampaignPeriodValid =
    //     hubspotDeal?.startDate &&
    //     (isAfter(new Date(hubspotDeal?.startDate), new Date()) ||
    //       isSameDay(new Date(hubspotDeal?.startDate), new Date()));
    //   if (isCampaignPeriodValid) {
    //     // if campaign has correct start date it should have correct endDate too
    //     setValue(
    //       'period',
    //       { startDate: hubspotDeal?.startDate || '', endDate: hubspotDeal?.endDate || '' },
    //       { shouldValidate: true }
    //     );
    //   }

    //   setValue('title', hubspotDeal?.dealName || '', { shouldValidate: true });
    // }
  };

  return (
    <>
      <Form.SectionTitle index={1} label={t('Campaign Information')} />

      {!isAdvertiser ? (
        <Form.Fields itemsPerRow={2}>
          {isAdminStaffAgencyTalentAgencyPartner ? (
            <AsyncSearchAdvertiserField<EngagementCampaignFormKeys>
              name="advertiserId"
              help={t('Tooltip.Advertiser')}
              title={t('Selector.Advertiser')}
              disabled={disabledFields.advertiserId}
              required
            />
          ) : null}

          {isAdminStaff ? (
            <AsyncSearchHubspotDealField<EngagementCampaignFormKeys>
              limit={1}
              name="hubspotDealIds"
              onChange={handleHubspotDealsChange}
              disabled={disabledFields?.hubspotDealIds}
              multiple
            />
          ) : null}

          {isAdminStaffTalentAgencyPartner && (
            <>
              <SelectorField<EngagementCampaignFormKeys>
                name="salesPics"
                options={allPicsUserOptions}
                help={t('Tooltip.Sales PIC')}
                title={t('Selector.Sales PIC')}
                disabled={disabledFields?.salesPics}
                hasSelectAll={false}
                multiple
                required
              />

              <SelectorField<EngagementCampaignFormKeys>
                options={allPicsUserOptions}
                name="influencerManagementPics"
                help={t('Tooltip.Influencer Management PIC')}
                title={t('Selector.Influencer Management PIC')}
                disabled={disabledFields?.influencerManagementPics}
                hasSelectAll={false}
                multiple
                required
              />
            </>
          )}
        </Form.Fields>
      ) : null}

      <TextField<EngagementCampaignFormKeys>
        name="title"
        disabled={disabledFields?.title}
        help={t('Tooltip.Campaign Title')}
        title={t('TextForm.Campaign Title')}
        placeholder={t<string>('TextForm.InstagramCampaign')}
        required
        dynamicError
      />

      <FileUploadField<EngagementCampaignFormKeys>
        name="thumbnail"
        target="thumbnail"
        itemsPerRow={3}
        title={t('General.CampaignThumbnail')}
        disabled={disabledFields?.thumbnail}
        help={t('Tooltip.CampaignThumbnailHelp')}
      />

      <Form.Fields>
        <Form.Field className={canOnlyEditEndDate ? 'flex-2' : 'flex-1'}>
          <Label title={t('Selector.Campaign Duration')} help={'Campaign Duration'} required />
          {canOnlyEditEndDate ? (
            <Form.TwoDayPickersInARow>
              <SingleDatePickerField className="p-0!" name="period.startDate" disabled />
              <span className="px-2">{'\u2013'}</span>
              <SingleDatePickerField
                name="period.endDate"
                disabled={disabledFields.period}
                className="p-0!"
                disabledDays={{ before: new Date() }}
              />
            </Form.TwoDayPickersInARow>
          ) : (
            <RangeDatePickerField
              className="p-0!"
              disabledRange={{ before: new Date() }}
              name={{ startDate: 'period.startDate', endDate: 'period.endDate' }}
            />
          )}
        </Form.Field>

        <SelectorField<EngagementCampaignFormKeys>
          name="countryIds"
          options={allCountryOptions}
          onChange={handleCountryIdsChange}
          disabled={disabledFields?.countryIds}
          title={t('Selector.Campaign Country')}
          help={t('Tooltip.CampaignCountryHelp')}
          hasSelectAll={false}
          multiple
          required
        />

        <SelectorField<EngagementCampaignFormKeys>
          name="campaignCategoryId"
          options={allCampaignCategoryOptions}
          help={t('Tooltip.Campaign Category')}
          title={t('Selector.Campaign Category')}
          disabled={disabledFields?.campaignCategoryId}
          required
        />
      </Form.Fields>

      <TextareaField<EngagementCampaignFormKeys>
        name="objective"
        help={t('Tooltip.Campaign Help')}
        disabled={disabledFields?.objective}
        title={t('TextForm.Campaign Objective')}
        placeholder={t<string>('TextForm.Campaign Objective Placeholder')}
        required
      />

      <Form.Fields className="mb-4">
        <TextField<EngagementCampaignFormKeys>
          name="budget"
          placeholder="1000000"
          help={t('Tooltip.Budget')}
          className="max-w-60"
          title={t('TextForm.Budget')}
          disabled={disabledFields?.budget}
          rightSection={currencyValue ? t(currencyValue) : undefined}
          required
          type="number"
        />

        {isAdminStaffAgencyTalentAgencyPartner && (
          <TextField<EngagementCampaignFormKeys>
            rightSection="%"
            placeholder="10"
            name="agencyMarginRate"
            className="max-w-41"
            help={t('Tooltip.Agency Rate Help Text')}
            disabled={isAdvertiser || disabledFields?.agencyMarginRate}
            title={t(
              !isAgencyTalentAgencyPartner ? 'TextForm.Ad Agency Margin Rate' : 'TextForm.Ad Agency profit rate'
            )}
          />
        )}
      </Form.Fields>

      <Accordion type="single" collapsible>
        <AccordionPrimitive.AccordionItem
          value="product information"
          className={cn('rounded-sm border-base-border py-1.5', 'not-data-[state=open]:hover:border-base-border-hover')}
        >
          <AccordionTrigger hasIcon={false} className="hover:bg-white">
            <Icon icon="caret-down" size={9} variant="black" className="group-data-[state=open]:rotate-180" />
            <p className="text-base">{t('Product Information')}</p>
            <p className="ml-auto text-sm text-tertiary">{t('optional')}</p>
          </AccordionTrigger>
          <AccordionContent className="border-t-0 bg-white">
            <RichEditorField<EngagementCampaignFormKeys>
              name="serviceInformation"
              title={t('TextForm.Service Info')}
              disabled={disabledFields?.serviceInformation}
              help={t('Tooltip.Product or Service Information')}
              placeholder={t<string>('TextForm.Service Information Placeholder')}
            />

            <FileUploadField<EngagementCampaignFormKeys>
              name="materials"
              target="material"
              itemsPerRow={3}
              title={t('General.CampaignMaterial')}
              disabled={disabledFields?.materials}
              help={t('Tooltip.CampaignMaterialHelp')}
              multiple
            />

            <TextField<EngagementCampaignFormKeys>
              name="serviceUrl"
              placeholder="https://anytag.jp/"
              title={t('TextForm.Service URL')}
              help={t('Tooltip.Service URL Help')}
              disabled={disabledFields?.serviceUrl}
            />

            <Form.Field className="mb-0">
              <TextField<EngagementCampaignFormKeys>
                placeholder="DL Link"
                name="campaignMaterialLink"
                help={t('Tooltip.CampaignMaterialLinkHelp')}
                disabled={disabledFields?.campaignMaterialLink}
                title={t('TextForm.Campaign material link (Google drive, Dropbox etc.)')}
              />
            </Form.Field>
          </AccordionContent>
        </AccordionPrimitive.AccordionItem>
      </Accordion>
    </>
  );
};
